import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Upload, FileText, GitCompare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";
import QuoteMergePanel from "./QuoteMergePanel";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2,
  maximumFractionDigits: 2 })}`;
export const QUOTE_TONE = {
  received: "bg-sky-100 text-sky-800",
  superseded: "bg-slate-100 text-slate-500 line-through",
  expired: "bg-amber-100 text-amber-800",
  applied: "bg-emerald-100 text-emerald-800",
};

/** Vendor quotes filed against a PO: drop the e-mail or the PDF, see what was read, compare
 *  and merge. The estimate-side quote reader is a different thing and is untouched. */
export default function QuoteDialog({ project, po, canEdit, onClose, onChanged }) {
  const [quotes, setQuotes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [comparing, setComparing] = useState(null);
  const [detail, setDetail] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { setQuotes((await opsApi.poQuotes(project.id, po.id)).quotes); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load the quotes"); }
  }, [project.id, po.id]);
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const res = await opsApi.poQuoteUpload(project.id, po.id, Array.from(files));
      res.results.forEach((r) => {
        if (r.duplicate) toast.info(r.message);
        else toast.success(`Filed ${r.quote.quote_ref || r.file}${r.superseded?.length
          ? ` — replaces ${r.superseded.length} earlier quote(s)` : ""}`);
      });
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "That file could not be read"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (comparing) {
    return (
      <QuoteMergePanel project={project} po={po} quote={comparing} canEdit={canEdit}
                       onBack={() => { setComparing(null); load(); }}
                       onApplied={() => { setComparing(null); load(); onChanged?.(); }} />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
         onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="ops-po-quote-dialog">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Vendor quotes · {po.po_number}</h3>
            <p className="mt-1 text-xs text-slate-500">
              Drop the vendor's e-mail (.eml, attachments included) or the quote PDF. The quote
              number, dates, totals, freight, lead time and line prices are read from it — nothing
              touches the PO until you compare and apply.
            </p>
          </div>
          {canEdit && (
            <>
              <input ref={fileRef} type="file" multiple accept=".eml,.pdf,.docx,.txt,.csv"
                     className="hidden" data-testid="ops-po-quote-file"
                     onChange={(e) => upload(e.target.files)} />
              <Button variant="data" size="sm" onClick={() => fileRef.current?.click()} disabled={busy}
                      data-testid="ops-po-quote-upload">
                {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  : <Upload className="mr-1.5 h-3.5 w-3.5" />} Add a quote
              </Button>
            </>
          )}
        </div>

        {quotes === null ? (
          <p className="mt-4 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
        ) : quotes.length === 0 ? (
          <div className="mt-4 rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500"
               data-testid="ops-po-quote-empty">
            No vendor quote on this PO yet.
          </div>
        ) : (
          <ul className="mt-4 divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="ops-po-quote-list">
            {quotes.map((q) => (
              <li key={q.id} className="px-3 py-2.5 text-xs" data-testid={`ops-po-quote-${q.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <FileText className="h-3.5 w-3.5 text-slate-400" />
                  <span className="font-semibold text-slate-900">{q.quote_ref || "No quote #"}</span>
                  <span className="text-slate-600">{q.vendor || q.manufacturer || "Vendor not read"}</span>
                  <span className={`rounded-full px-2 py-0.5 font-medium ${QUOTE_TONE[q.status] || ""}`}
                        data-testid={`ops-po-quote-status-${q.id}`}>{q.status_label}</span>
                  <span className="ml-auto font-medium text-slate-800">{q.total ? money(q.total) : "total not read"}</span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-slate-500">
                  <span>Quoted {q.quote_date || "—"}</span>
                  <span>Valid through {q.valid_through || "—"}{q.days_left !== null && q.status === "received"
                    ? ` (${q.days_left} day${q.days_left === 1 ? "" : "s"} left)` : ""}</span>
                  <span>{q.line_count} line{q.line_count === 1 ? "" : "s"}</span>
                  {q.freight > 0 && <span>Freight {money(q.freight)}</span>}
                  {q.lead_time && <span>Lead time {q.lead_time}</span>}
                  <span className="text-slate-400">read {q.parse?.method}
                    {q.parse?.missing?.length ? ` · could not read: ${q.parse.missing.join(", ")}` : ""}</span>
                  <button className="text-[#007BFF] hover:underline" onClick={() => setDetail(detail === q.id ? null : q.id)}
                          data-testid={`ops-po-quote-lines-${q.id}`}>{detail === q.id ? "Hide lines" : "Show lines"}</button>
                  {q.status === "applied" && q.applied && (
                    <span className="text-emerald-700">Applied {q.applied_at?.slice(0, 10)} by {q.applied_by} · {q.applied.lines?.length} line(s)</span>
                  )}
                  {q.superseded_by && <span>Replaced by a newer quote</span>}
                  {["received", "applied"].includes(q.status) && po.status === "prepared" && (
                    <Button size="sm" variant="outline" className="h-7" onClick={() => setComparing(q)}
                            data-testid={`ops-po-quote-compare-${q.id}`}>
                      <GitCompare className="mr-1 h-3 w-3" /> Compare &amp; merge
                    </Button>
                  )}
                  {["received", "applied"].includes(q.status) && po.status !== "prepared" && (
                    <Button size="sm" variant="ghost" className="h-7" onClick={() => setComparing(q)}
                            data-testid={`ops-po-quote-compare-${q.id}`}>
                      <GitCompare className="mr-1 h-3 w-3" /> Compare
                    </Button>
                  )}
                </div>
                {detail === q.id && (
                  <table className="mt-2 w-full text-[11px]" data-testid={`ops-po-quote-table-${q.id}`}>
                    <thead className="text-slate-500"><tr>
                      <th className="text-left font-normal">Part</th><th className="text-left font-normal">Description</th>
                      <th className="text-right font-normal">Qty</th><th className="text-right font-normal">Unit</th>
                      <th className="text-right font-normal">Amount</th></tr></thead>
                    <tbody>
                      {(q.line_items || []).map((l, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="py-0.5 font-mono">{l.item_number || "—"}</td>
                          <td className="py-0.5">{l.description}</td>
                          <td className="py-0.5 text-right">{l.qty}</td>
                          <td className="py-0.5 text-right">{money(l.unit_price)}</td>
                          <td className="py-0.5 text-right">{money(l.amount)}</td>
                        </tr>
                      ))}
                      {!(q.line_items || []).length && <tr><td colSpan={5} className="py-1 text-slate-400">No line rows were read from this quote.</td></tr>}
                    </tbody>
                  </table>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 flex justify-end">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="ops-po-quote-close">Close</Button>
        </div>
      </div>
    </div>
  );
}
